import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import LinearProgress from "@material-ui/core/LinearProgress";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import {
  createCombinedReportRequest,
  listenToRequestStatus,
  getDownloadURL,
} from "../../data/report-store";

const useStyles = makeStyles((theme) => ({
  progress: {
    marginTop: theme.spacing(2),
  },
  error: {
    color: theme.palette.error.main,
  },
}));

const makeStatusText = (status, count) => {
  switch (status) {
    case "requesting":
      return `Requesting a master report for ${count} inspections…`;
    case "requested":
      return "Queued for building…";
    case "building":
      return "Building your master report. Combining this many inspections can take a few minutes — you can leave this open.";
    case "done":
      return "Your master report is ready.";
    default:
      return "";
  }
};

function MasterReportDialog(props) {
  const { open, inspectionIds, reportType, onClose } = props;
  const classes = useStyles();

  const [status, setStatus] = useState("requesting");
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [error, setError] = useState(null);

  const count = inspectionIds.length;
  const finished = status === "done" || Boolean(error);

  useEffect(() => {
    if (!open) return undefined;

    // Guards against a late callback resolving after the dialog is dismissed,
    // which would set state on an unmounted subtree.
    let cancelled = false;
    const listener = {};

    const handleError = (caught) => {
      if (cancelled) return;
      setError(caught.message ?? "The master report could not be generated.");
    };

    const handleStatusChange = async (nextStatus, requestPath) => {
      if (cancelled) return;
      setStatus(nextStatus);

      if (nextStatus !== "done") return;

      try {
        const url = await getDownloadURL(requestPath);
        if (!cancelled) setDownloadUrl(url);
      } catch (caught) {
        handleError(caught);
      }
    };

    const request = async () => {
      setStatus("requesting");
      setDownloadUrl(null);
      setError(null);

      try {
        const requestPath = await createCombinedReportRequest(
          inspectionIds,
          reportType
        );

        if (cancelled) return;

        listener.cleanup = listenToRequestStatus(
          requestPath,
          handleStatusChange,
          handleError
        );
      } catch (caught) {
        handleError(caught);
      }
    };

    request();

    return () => {
      cancelled = true;
      if (listener.cleanup) listener.cleanup();
    };
  }, [open]);

  return (
    <Dialog open={open} maxWidth="xs" fullWidth onClose={onClose}>
      <DialogTitle>Master Report</DialogTitle>
      <DialogContent>
        {error ? (
          <DialogContentText className={classes.error}>
            {error}
          </DialogContentText>
        ) : (
          <DialogContentText>{makeStatusText(status, count)}</DialogContentText>
        )}
        {!finished && (
          <React.Fragment>
            <LinearProgress className={classes.progress} />
            <Typography variant="caption" color="textSecondary">
              {reportType === "xlsx" ? "Excel workbook" : "PDF"}
            </Typography>
          </React.Fragment>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {finished ? "Close" : "Run in background"}
        </Button>
        {downloadUrl && (
          <Button
            color="primary"
            variant="contained"
            href={downloadUrl}
            onClick={onClose}
          >
            Download
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

MasterReportDialog.propTypes = {
  open: PropTypes.bool,
  inspectionIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  reportType: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default MasterReportDialog;
